import { useState, useMemo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../../../store/hooks'
import { setOpenDropdown } from '../../../../../../../store/ui/uiSlice'
import { selectOpenDropdownId } from '../../../../../../../store/ui/uiSelectors'
import { useWorkspace } from '../../../../../../../contexts/WorkspaceContext'
import { addLabelSaga } from '../../../../../../../store/workspace/workspaceSlice'
import { resolveTaskLabels, toggleLabelId } from '@/utils/taskLabels'
import { labelColor } from '@/const/colors'
import ButtonRound from '@/components/ui/ButtonRound/ButtonRound'
import LabelDot from '@/components/ui/LabelChip/LabelDot'
import DropdownWrapper from '@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper'
import Dropdown from '../../../../../../ui/Dropdown/Dropdown'
import DropdownItem from '@/components/ui/Dropdown/components/DropdownItem/DropdownItem'
import DropdownDivider from '@/components/ui/Dropdown/components/DropdownDivider/DropdownDivider'
import DropdownInput from '@/components/ui/Dropdown/components/DropdownInput/DropdownInput'
import IconCheck from '@/assets/icons/icon-check.svg?react'
import css from './TaskModalLabels.module.css'

interface TaskModalLabelsProps {
    taskId: string
    labelIds: string[] | null | undefined
    onChange: (labelIds: string[]) => void
}

const DROPDOWN_ID = 'task-modal-labels'

const TaskModalLabels = ({ taskId, labelIds, onChange }: TaskModalLabelsProps) => {
    const dispatch = useAppDispatch()
    const openDropdownId = useAppSelector(selectOpenDropdownId)
    const isDropdownOpen = openDropdownId === DROPDOWN_ID
    const { labels } = useWorkspace()
    const [search, setSearch] = useState('')

    // Resolving here (not off task.labels directly) drops ids of deleted labels, so the modal
    // never shows a blank chip for one.
    const selected = useMemo(() => resolveTaskLabels(labelIds, labels), [labelIds, labels])
    const selectedIds = useMemo(() => new Set(selected.map(l => l.id)), [selected])

    // The menu groups the task's labels above the rest, but that grouping is captured when the
    // dropdown opens and held until it closes: re-grouping on every toggle would slide the item
    // out from under the cursor, so ticking two labels in a row would hit the wrong one.
    const frozenSelectionRef = useRef<Set<string>>(new Set())
    if (!isDropdownOpen) frozenSelectionRef.current = selectedIds
    const frozenSelection = frozenSelectionRef.current

    const query = search.trim().toLowerCase()
    const { onTask, rest } = useMemo(() => {
        const matching = labels.filter(label => (label.name || '').toLowerCase().includes(query))
        return {
            onTask: matching.filter(label => frozenSelection.has(label.id)),
            rest: matching.filter(label => !frozenSelection.has(label.id)),
        }
    }, [labels, query, frozenSelection])

    // Offer creation only for a name nobody used yet — an exact match is already in the list.
    const canCreate = query.length > 0 && !labels.some(label => (label.name || '').toLowerCase() === query)
    const hasResults = onTask.length + rest.length > 0

    // Always sends the FULL label array, never a delta. The write lands on updateTaskSaga
    // (takeLatest), so checking three labels quickly cancels the first two writes mid-flight —
    // harmless only because each dispatch carries the cumulative list built off the optimistic
    // store, so the surviving last write still persists all three. A delta payload here would
    // silently lose labels (the SCD-905 failure mode).
    const handleToggle = (labelId: string) => {
        onChange(toggleLabelId(labelIds, labelId))
    }

    const handleCreate = () => {
        const name = search.trim()
        if (!name) return
        // The saga attaches the new label to the task once the doc exists — the generated id
        // isn't knowable here.
        dispatch(addLabelSaga({ name, attachToTaskId: taskId }))
        setSearch('')
        dispatch(setOpenDropdown(null))
    }

    const handleButtonClick = () => {
        setSearch('')
        dispatch(setOpenDropdown(isDropdownOpen ? null : DROPDOWN_ID))
    }

    const handleClose = () => {
        dispatch(setOpenDropdown(null))
    }

    const renderOption = (label: typeof labels[number]) => (
        <DropdownItem
            key={label.id}
            icon={<span className={css.swatch} style={{ background: labelColor(label.color) }} />}
            title={label.name || 'Untitled'}
            rightIcon={selectedIds.has(label.id) ? <IconCheck /> : undefined}
            onClick={() => handleToggle(label.id)}
            testId="label-option"
        />
    )

    // The trigger is a plain ButtonRound — same component as the project and assignee buttons
    // beside it — so its size matches theirs by construction rather than by copied CSS. It stays
    // grey; the label's color shows only as a dot in the icon slot, the same dot LabelChip uses in
    // task rows. Only the first label is named (plus a "+N" count, the rule the task row uses);
    // the menu is where all of them are visible.
    const trigger = selected.length > 0 ? (
        <ButtonRound
            icon={<LabelDot color={selected[0].color} />}
            onClick={handleButtonClick}
            testId="task-labels-btn"
        >
            {selected[0].name || 'Untitled'}
            {selected.length > 1 && ` +${selected.length - 1}`}
        </ButtonRound>
    ) : (
        <ButtonRound onClick={handleButtonClick} testId="task-labels-btn">
            Labels
        </ButtonRound>
    )

    return (
        <DropdownWrapper>
            {trigger}
            <Dropdown isOpen={isDropdownOpen} onClose={handleClose}>
                <DropdownInput
                    placeholder="Search or create..."
                    value={search}
                    onChange={setSearch}
                    onSubmit={handleCreate}
                    autoFocus
                />
                <DropdownDivider />
                {onTask.map(renderOption)}
                {onTask.length > 0 && rest.length > 0 && <DropdownDivider />}
                {rest.map(renderOption)}
                {!hasResults && !canCreate && (
                    <DropdownItem title="No labels yet" disabled />
                )}
                {canCreate && (
                    <>
                        {hasResults && <DropdownDivider />}
                        <DropdownItem
                            title={`Create "${search.trim()}"`}
                            onClick={handleCreate}
                            testId="create-label-btn"
                        />
                    </>
                )}
            </Dropdown>
        </DropdownWrapper>
    )
}

export default TaskModalLabels
